import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";

function todayISOEuropeRome() {
  // Approximate: use browser locale to get a YYYY-MM-DD in Europe/Rome.
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Rome",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}

const REASON_STYLE = {
  "no story_ideas doc created (cron skipped this client)": {
    label: "Cron non l'ha processato",
    cls: "bg-red-50 text-red-700 border border-red-200",
  },
  "stuck in generating=True (LLM likely crashed mid-generation)": {
    label: "Bloccato in generazione",
    cls: "bg-amber-50 text-amber-800 border border-amber-200",
  },
  "doc exists but ideas empty": {
    label: "Idee vuote",
    cls: "bg-amber-50 text-amber-800 border border-amber-200",
  },
  "generated but Telegram send never succeeded": {
    label: "Telegram non inviato",
    cls: "bg-orange-50 text-orange-800 border border-orange-200",
  },
};

function StatCard({ label, value, tone = "neutral", testId }) {
  const toneCls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50"
      : tone === "bad"
      ? "border-red-200 bg-red-50"
      : "border-[var(--mp-border,#e5e7eb)] bg-white";
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${toneCls} p-4`}
    >
      <div className="text-xs uppercase tracking-wide text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-3xl font-semibold text-[var(--mp-ink,#111)]">
        {value}
      </div>
    </div>
  );
}

export default function AdminStoryDiagnostics() {
  const [date, setDate] = useState(todayISOEuropeRome());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (d) => {
      setLoading(true);
      try {
        const res = await api.get("/admin/story-ideas/diagnostics", {
          params: { date: d },
        });
        setData(res.data);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(date);
  }, []);

  const summary = data?.summary || {};
  const missing = data?.missing || [];

  const deliveryRatio = useMemo(() => {
    if (!summary.with_telegram) return null;
    return Math.round((summary.delivered_telegram / summary.with_telegram) * 100);
  }, [summary]);

  const overallTone =
    deliveryRatio == null
      ? "neutral"
      : deliveryRatio >= 90
      ? "good"
      : deliveryRatio >= 50
      ? "warn"
      : "bad";

  return (
    <div className="space-y-6" data-testid="admin-story-diagnostics">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--mp-ink,#111)] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[var(--mp-primary,#e11d48)]" />
            Diagnostica idee giornaliere
          </h1>
          <p className="mt-1 text-sm text-neutral-600 max-w-2xl">
            Controlla in un colpo d&apos;occhio quanti clienti Pro hanno ricevuto le
            idee via Telegram oggi. Se qualcuno manca all&apos;appello, qui trovi il
            motivo esatto.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label
              htmlFor="diag-date"
              className="block text-xs font-medium text-neutral-500 mb-1"
            >
              Data (Europe/Rome)
            </label>
            <Input
              id="diag-date"
              data-testid="diag-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-44"
            />
          </div>
          <Button
            data-testid="diag-refresh-btn"
            onClick={() => load(date)}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <StatCard
              label="Clienti Pro totali"
              value={summary.pro_clients ?? 0}
              testId="stat-pro-clients"
            />
            <StatCard
              label="Con Telegram linkato"
              value={summary.with_telegram ?? 0}
              testId="stat-with-telegram"
            />
            <StatCard
              label="Consegnate su Telegram"
              value={summary.delivered_telegram ?? 0}
              tone={overallTone}
              testId="stat-delivered"
            />
            <StatCard
              label="Copertura consegna"
              value={deliveryRatio == null ? "—" : `${deliveryRatio}%`}
              tone={overallTone}
              testId="stat-ratio"
            />
            <StatCard
              label="Documenti creati"
              value={summary.doc_created ?? 0}
              testId="stat-doc-created"
            />
            <StatCard
              label="Idee generate correttamente"
              value={summary.generated_ok ?? 0}
              testId="stat-generated-ok"
            />
            <StatCard
              label="Bloccati in generazione"
              value={summary.still_generating ?? 0}
              tone={summary.still_generating ? "bad" : "neutral"}
              testId="stat-still-generating"
            />
            <StatCard
              label="Da inviare (TG in sospeso)"
              value={summary.pending_delivery ?? 0}
              tone={summary.pending_delivery ? "warn" : "neutral"}
              testId="stat-pending"
            />
          </div>

          <div className="rounded-2xl border border-[var(--mp-border,#e5e7eb)] bg-white">
            <div className="px-5 py-4 border-b border-[var(--mp-border,#e5e7eb)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                {missing.length === 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
                <h2 className="text-lg font-semibold text-[var(--mp-ink,#111)]">
                  {missing.length === 0
                    ? "Tutti i clienti con Telegram hanno ricevuto le idee"
                    : `Clienti che non hanno ricevuto le idee (${missing.length})`}
                </h2>
              </div>
              <Badge className="bg-neutral-100 text-neutral-700 hover:bg-neutral-100">
                {data.date}
              </Badge>
            </div>

            {missing.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-neutral-500">
                Nessun cliente Pro con Telegram linkato risulta mancante per
                questa data. 🎉
              </div>
            ) : (
              <ul
                data-testid="missing-list"
                className="divide-y divide-[var(--mp-border,#e5e7eb)]"
              >
                {missing.map((m) => {
                  const style =
                    REASON_STYLE[m.reason] || {
                      label: m.reason,
                      cls: "bg-neutral-100 text-neutral-700 border border-neutral-200",
                    };
                  return (
                    <li
                      key={m.client_id}
                      data-testid={`missing-row-${m.client_id}`}
                      className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-[var(--mp-ink,#111)] truncate">
                          {m.name_salone || "(salone senza nome)"}
                        </div>
                        <div className="text-xs text-neutral-500 truncate">
                          {m.email}
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full ${style.cls}`}
                      >
                        {style.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="rounded-xl border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
          Nessun dato caricato. Premi &quot;Aggiorna&quot; per vedere lo stato di oggi.
        </div>
      )}
    </div>
  );
}
